import { checkEmail } from '@shef/validators';
import { ShefErrorCode } from 'common/errors/ShefErrors';
import { ClientEvents } from 'common/events/ClientEvents';
import { isLeft } from 'fp-ts/lib/Either';
import { GraphQLError } from 'graphql';
import { includes, isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import {
  AuthResult,
  CurrentUserFieldsFragment,
  OAuthLoginType,
  OAuthMissingFieldsResult,
  useGetRegisteredEmailLazyQuery,
} from 'src/gqlReactTypings.generated.d';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useGateType } from './GateTypeContext';
import { useLeadCaptureAutofill } from './useLeadCaptureAutofill';

export enum AuthType {
  GOOGLE,
  EMAIL,
  APPLE,
}

export interface EntryFormError {
  type: AuthType;
  errorMessage: string;
}

export interface EntryFormProps {
  email: string;
  onEmailChange: (event: React.FormEvent<HTMLInputElement>) => void;
  onEmailContinueSuccess: (email: string, isRegisteredUser: boolean) => void;
  onOAuthSuccess: (isExistingUser: boolean, currentUser: CurrentUserFieldsFragment) => void;
  onOAuthMissingFields: (type: OAuthLoginType, token: string, missingFields: OAuthMissingFieldsResult) => void;
  onOAuthAdditionalFields: (
    type: OAuthLoginType,
    user: AuthResult,
    token: string,
    isExistingUser: boolean,
    onOAuthSuccess: (isExistingUser: boolean, currentUser: CurrentUserFieldsFragment) => void
  ) => void;
  requireZipCode?: boolean;
  signUpEntryPoint?: string | null;
  continueCTA?: string | null;
}

const isGraphQLError = (error: Error): error is GraphQLError => 'extensions' in error;

const ALLOW_LISTED_ERROR_CODES = [ShefErrorCode.FACEBOOK_SIGN_UP_NO_EMAIL];
const isAllowListedError = (error: Error): boolean => {
  if (!isGraphQLError(error)) {
    return false;
  }

  return includes(ALLOW_LISTED_ERROR_CODES, error.extensions.code);
};

/**
 * `googleProps` and `appleProps` come back whole rather than assembled at each call site. Both
 * providers take the same seven props, and missing one silently drops an error path.
 */
export const useLoginOrSignUpEntryForm = ({
  email,
  onEmailChange,
  onEmailContinueSuccess,
  onOAuthSuccess,
  onOAuthMissingFields,
  onOAuthAdditionalFields,
  requireZipCode,
  signUpEntryPoint,
}: EntryFormProps) => {
  const [error, setError] = useState<EntryFormError | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const signInWithAppleEnabled = useFeatureFlag('appleSignInOnWebEnabled');
  const tracker = useTracker();
  const gateType = useGateType();

  useLeadCaptureAutofill({ email, onEmailChange });

  const resetErrors = () => {
    setError(undefined);
    setEmailError(undefined);
  };

  const [getRegisteredEmail, { error: getRegisteredEmailError, data: getRegisteredEmailData }] =
    useGetRegisteredEmailLazyQuery({ fetchPolicy: 'network-only' });

  /**
   * So annoyed with this hack – as far as I can tell LazyQuery doesn't give you a good callback or promised-based way of getting the GraphQLErrors, so
   * so have to use useEffect. In addition, the lazyQuery loading is not set to true when the query is retried, so we have to hack around with our own.
   */
  const [getRegisteredEmailLoading, setGetRegisteredEmailLoading] = useState(false);

  useEffect(() => {
    if (getRegisteredEmailLoading) {
      return;
    }

    if (!isNil(getRegisteredEmailError)) {
      const invalidEmailErrors = getRegisteredEmailError.graphQLErrors.filter(
        (graphQLError) => graphQLError.extensions?.code === ShefErrorCode.INVALID_EMAIL
      );
      if (invalidEmailErrors.length > 0) {
        setError({ type: AuthType.EMAIL, errorMessage: invalidEmailErrors[0].message });
      } else {
        setError({
          type: AuthType.EMAIL,
          errorMessage: 'There was an issue processing your request. Please try again.',
        });
      }
    } else if (getRegisteredEmailData) {
      const registeredEmail = getRegisteredEmailData.getRegisteredEmail;
      const userIsRegistered = !isNil(registeredEmail);

      onEmailContinueSuccess(registeredEmail ?? email, userIsRegistered);
    }
  }, [getRegisteredEmailError?.message, getRegisteredEmailData?.getRegisteredEmail, getRegisteredEmailLoading]);

  const handleEmailContinue = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (getRegisteredEmailLoading) {
      return;
    }

    resetErrors();

    const emailCheck = checkEmail(email);
    if (isLeft(emailCheck)) {
      setEmailError(emailCheck.left);
    } else {
      tracker.track(ClientEvents.LOGIN_MODAL_EMAIL_ENTERED, { signUpEntryPoint, gateType });
      getRegisteredEmail({ variables: { email: email.trim() } }).then(() => setGetRegisteredEmailLoading(false));
      setGetRegisteredEmailLoading(true);
    }
  };

  // Theoretically this shouldn't be needed – not sure why enter isn't handling form submission like normal
  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEmailContinue();
    }
  };

  const displayError = (type: AuthType, providerName: string) => (error: Error) => {
    const message = isAllowListedError(error)
      ? error.message
      : `There was an issue logging in with ${providerName}. Please try again.`;
    setError({ type, errorMessage: message });
  };

  const oAuthProps = {
    onMissingFields: onOAuthMissingFields,
    onAdditionalFields: onOAuthAdditionalFields,
    onAttempt: resetErrors,
    onSuccess: onOAuthSuccess,
    requireZipCode,
    signUpEntryPoint,
  };

  return {
    error,
    emailError,
    loading: getRegisteredEmailLoading,
    handleEmailContinue,
    handleKeyDown,
    showApple: signInWithAppleEnabled,
    googleProps: { ...oAuthProps, onError: displayError(AuthType.GOOGLE, 'Google') },
    appleProps: { ...oAuthProps, onError: displayError(AuthType.APPLE, 'Apple') },
  };
};
